import { ShieldAlert } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { Role } from '@shared/types'
import InstanceSettingsCard from '@/components/admin/InstanceSettingsCard'
import UsersTable from '@/components/admin/UsersTable'
import PageHeader from '@/components/layout/PageHeader'
import { Card, EmptyState, SegmentedControl } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'

const TABS = [
  { value: 'users', label: 'Users' },
  { value: 'instance', label: 'Instance settings' }
]

export default function AdminPage(): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'instance' ? 'instance' : 'users'

  // The route is already guarded, but a shared /admin link should still explain
  // itself rather than render an empty shell.
  if (!user || user.role !== Role.Admin) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <PageHeader title="Admin" description="Accounts and instance-wide settings." />
        <Card>
          <EmptyState
            icon={<ShieldAlert />}
            title="Admins only"
            description="This area manages every account on the instance and the settings that apply to all of them. Your account does not have the admin role, so there is nothing here for you to change."
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Admin"
        description="Decide who can sign in, and how this instance behaves for everyone on it."
      />

      <SegmentedControl
        label="Admin section"
        className="mb-6"
        value={tab}
        options={TABS}
        onChange={(value) => {
          setParams(value === 'users' ? {} : { tab: value }, { replace: true })
        }}
      />

      {tab === 'users' ? <UsersTable /> : <InstanceSettingsCard />}
    </div>
  )
}

export { AdminPage }
